import { useQuery } from '@tanstack/react-query';
import { adminService } from '@services/adminService';
import { DollarSign, TrendingDown, Users, CreditCard } from 'lucide-react';

const fmt = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const StatCard = ({ label, value, sub, color = 'text-white' }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
    <p className="text-gray-400 text-sm">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
);

const PLAN_LABELS = { pro: 'Pro', family: 'Family' };

const AdminRevenue = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'revenue'],
    queryFn: () => adminService.getRevenue(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const d = data || {};

  return (
    <div>
      <h1 className="text-white text-2xl font-bold mb-1">Receita</h1>
      <p className="text-gray-400 text-sm mb-6">MRR, ARR e métricas financeiras</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard label="MRR" value={fmt(d.mrr)} sub="Receita mensal recorrente" color="text-green-400" />
        <StatCard label="ARR" value={fmt(d.arr)} sub="Receita anual recorrente" color="text-green-300" />
        <StatCard label="Churn" value={`${d.churnRate ?? 0}%`} sub="Taxa de cancelamento" color={d.churnRate > 5 ? 'text-red-400' : 'text-white'} />
        <StatCard label="Clientes pagantes" value={d.payingCustomers ?? 0} sub="assinaturas ativas" />
        <StatCard label="Em trial" value={d.trialCustomers ?? 0} />
        <StatCard label="Cancelados" value={d.canceledCustomers ?? 0} />
      </div>

      {/* Revenue by plan */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-indigo-400" />
          Receita por plano
        </h2>
        {Object.keys(d.revenueByPlan || {}).length === 0 ? (
          <p className="text-gray-500 text-sm">Sem assinaturas ativas</p>
        ) : (
          <div className="divide-y divide-gray-800">
            {Object.entries(d.revenueByPlan || {}).map(([plan, info]) => (
              <div key={plan} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-4 w-4 text-indigo-400" />
                  <div>
                    <p className="text-white font-medium">{PLAN_LABELS[plan] || plan}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {info.count} assinantes
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-semibold">{fmt(info.mrr)}</p>
                  <p className="text-gray-500 text-xs">/mês</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRevenue;
